import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getProductReport } from "../../redux/apiCalls";
import { publicRequest } from "../../requestMethods";
import moment from "moment";
import excel from "../../components/excel/excel";
import Select from "react-select";
import { useHistory } from "react-router";
import Table from "../../components/advancedTable";
import {
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Bar,
    ResponsiveContainer,
    BarChart,
    PieChart,
    Pie,
    Cell,
} from "recharts";

const getIntroOfPage = (label) => {
    if (label === "Page A") {
        return "Page A is about men's clothing";
    }
    if (label === "Page B") {
        return "Page B is about women's dress";
    }
    if (label === "Page C") {
        return "Page C is about women's bag";
    }
    if (label === "Page D") {
        return "Page D is about household goods";
    }
    if (label === "Page E") {
        return "Page E is about food";
    }
    if (label === "Page F") {
        return "Page F is about baby food";
    }
    return "";
};

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div
                className="custom-tooltip p-8 font-bold bg-black"
                style={{ background: payload[0].payload.fill }}
            >
                <p className="bold label">{`${payload[0].payload.outlet} : ${payload[0].value} Sales`}</p>
                <p className="intro">{getIntroOfPage(label)}</p>
            </div>
        );
    }

    return null;
};

export default function OutletReport() {
    const dispatch = useDispatch();
    const history = useHistory();
    const currentUser = useSelector((state) => state.user.currentUser);

    const COLORS = [
        "#0088FE",
        "#00C49F",
        "#FFBB28",
        "#FF8042",
        "#8042ff",
        "#ff4263",
        "#56462a",
        "#2a5646",
        "#f8edeb",
        "#df99ad",
        "#ffff00",
    ];

    const NumberFormatter = new Intl.NumberFormat("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    const manager = useSelector((state) =>
        state.user.currentUser
            ? state.user.currentUser.type === "manager"
            : null
    );

    const superadmin = useSelector((state) =>
        state.user.currentUser ? state.user.currentUser.isSuperAdmin : null
    );

    const principle = useSelector((state) =>
        state.user.currentUser ? state.user.currentUser.principle : null
    );
    const buildDefaultInputs = useCallback(
        () => ({
            createdBy: currentUser?._id,
            principle: manager
                ? principle
                : currentUser
                ? currentUser._id
                : undefined,
            dateFrom: moment(Date.now()).startOf("year").format("YYYY-MM-DD"),
            dateTo: moment(Date.now()).add(1, "days").format("YYYY-MM-DD"),
            user: undefined,
        }),
        [currentUser?._id, manager, principle]
    );
    const [isLoaded, setIsLoaded] = useState(false);
    const [data, setData] = useState([]);
    const [salesData, setSalesData] = useState([]);
    const [usersData, setUsersData] = useState([]);
    const [resetKey, setResetKey] = useState(0);
    const [inputs, setInputs] = useState(buildDefaultInputs);

    const getAdmin = async () => {
        const res =
            process.env.REACT_APP_COUNTRY === "SINGAPORE"
                ? await publicRequest.get("/users/admin", {
                      params: { isAdmin: true },
                  })
                : await publicRequest.get("/users/superadmin", {
                      params: { isAdmin: true },
                  });
        setUsersData(res.data);
    };

    const handleChange = (e) => {
        setInputs((prev) => {
            return { ...prev, user: e.value };
        });
    };

    const handleDateFrom = (e) => {
        setInputs((prev) => {
            return { ...prev, dateFrom: e.target.value };
        });
    };
    const handleDateTo = (e) => {
        setInputs((prev) => {
            return { ...prev, dateTo: e.target.value };
        });
    };

    const handleResetFilters = () => {
        setInputs(buildDefaultInputs());
        setResetKey((prev) => prev + 1);
    };

    useEffect(() => {
        const fetchData = async () => {
            const res = await getProductReport(dispatch, inputs);

            if (res) {
                setIsLoaded(true);
                setData(res.product);
                setSalesData(res.sales);
            }
        };
        fetchData();
    }, [dispatch, inputs]);

    useEffect(() => {
        if (usersData[0] === undefined) {
            getAdmin();
        }
    }, [usersData]);

    const columns = [
        {
            accessor: "outlet",
            Header: "Outlet",
            sortable: false,
            Cell: ({ value }) => (
                <div className="initialPadding font-medium">{value || "-"}</div>
            ),
        },
        {
            accessor: "amount",
            Header: "Revenue",
            sortable: false,
            Cell: (params) => (
                <div className="initialPadding">
                    <span className="text-lg font-bold text-green-600">
                        {params.row.original.outlet === "HAOFA Tourbillon Singapore" ? "$" : "RM"}
                        {NumberFormatter.format(
                            params.row.original.amount ? params.row.original.amount : 0
                        )}
                    </span>
                </div>
            ),
        },
        {
            accessor: "unit",
            Header: "Units Sold",
            sortable: false,
            Cell: ({ value }) => (
                <div className="initialPadding">
                    <span className="inline-flex px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm font-medium">
                        {value || 0} units
                    </span>
                </div>
            ),
        },
        {
            accessor: "action",
            Header: "Actions",
            sortable: false,
            Cell: (params) => {
                return (
                    <div className="initialPadding">
                        {superadmin && (
                            <button
                                onClick={() =>
                                    history.push("/orders", {
                                        outlet: params.row.original.outletId,
                                    })
                                }
                                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium rounded-lg shadow-lg transition-all duration-300 transform hover:scale-105 flex items-center gap-2"
                            >
                                <span>📊</span>
                                <span>View Sales</span>
                            </button>
                        )}
                    </div>
                );
            },
        },
    ];

    const outletOptions = usersData.map((user) => ({
        label: `${user.name} - ${user.phoneNumber} - ${user.email}`,
        value: user._id,
    }));

    return (
        <div className="w-full bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
            <div className="productList bg-white rounded-3xl shadow-2xl p-8 m-6 border border-slate-200/50">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-slate-800 mb-2">Product Sales Analytics</h1>
                    <p className="text-slate-600">Comprehensive product performance and outlet sales analysis</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <div className="space-y-2">
                        <label className="text-sm font-semibold text-slate-700 tracking-wide">From Date</label>
                        <div className="luxury-input-wrapper">
                            <input
                                className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-300 font-medium text-slate-700"
                                name="dateFrom"
                                value={inputs.dateFrom}
                                type="date"
                                onChange={(e) => handleDateFrom(e)}
                                style={{
                                    background: 'linear-gradient(to right, rgb(248 250 252), rgb(239 246 255))',
                                    border: '2px solid rgb(226 232 240)',
                                    borderRadius: '12px',
                                    padding: '12px 16px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                }}
                            />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm font-semibold text-slate-700 tracking-wide">To Date</label>
                        <div className="luxury-input-wrapper">
                            <input
                                className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-300 font-medium text-slate-700"
                                name="dateTo"
                                value={inputs.dateTo}
                                type="date"
                                onChange={(e) => handleDateTo(e)}
                                style={{
                                    background: 'linear-gradient(to right, rgb(248 250 252), rgb(239 246 255))',
                                    border: '2px solid rgb(226 232 240)',
                                    borderRadius: '12px',
                                    padding: '12px 16px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                }}
                            />
                        </div>
                    </div>
                    {superadmin && (
                        <div className="space-y-2">
                            <label className="text-sm font-semibold text-slate-700 tracking-wide">Select Outlet</label>
                            <div className="luxury-select-wrapper">
                                <Select
                                    key={`outlet-${resetKey}`}
                                    options={outletOptions}
                                    value={
                                        outletOptions.find(
                                            (opt) => opt.value === inputs.user
                                        ) || null
                                    }
                                    onChange={(e) => handleChange(e)}
                                    placeholder="Select Outlet"
                                    onClick={() =>
                                        usersData[0] === undefined ? getAdmin() : {}
                                    }
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            background: 'linear-gradient(to right, rgb(248 250 252), rgb(239 246 255))',
                                            border: '2px solid rgb(226 232 240)',
                                            borderRadius: '12px',
                                            padding: '6px',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            boxShadow: 'none',
                                            '&:hover': {
                                                borderColor: 'rgb(59 130 246)',
                                            }
                                        }),
                                        option: (base, state) => ({
                                            ...base,
                                            backgroundColor: state.isSelected ? 'rgb(59 130 246)' : state.isFocused ? 'rgb(239 246 255)' : 'white',
                                            color: state.isSelected ? 'white' : 'rgb(51 65 85)',
                                            fontWeight: '500',
                                            '&:hover': {
                                                backgroundColor: 'rgb(239 246 255)',
                                            }
                                        })
                                    }}
                                />
                            </div>
                        </div>
                    )}
                    <div className="flex items-end gap-3 flex-wrap">
                        <button
                            type="button"
                            onClick={handleResetFilters}
                            className="px-6 py-3 bg-white text-slate-800 font-semibold rounded-xl shadow-md border border-slate-200 hover:border-blue-400 hover:shadow-lg transition-all duration-300 flex items-center gap-2"
                        >
                            <span className="text-blue-500">🔄</span>
                            <span>Reset Filters</span>
                        </button>
                        <button
                            onClick={() => {
                                const payload = salesData.map((item) => {
                                    return {
                                        Outlet: item.outlet,
                                        Unit: item.unit,
                                        Amount: item.amount,
                                    };
                                });
                                excel({
                                    excelData: payload,
                                    fileName: `Haofa-Sales-${moment(
                                        inputs.dateFrom
                                    ).format("DD-MM-YYYY")}-to-${moment(
                                        inputs.dateTo
                                    ).format("DD-MM-YYYY")}`,
                                });
                            }}
                            className="px-6 py-3 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-slate-800 font-bold rounded-xl shadow-xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl flex items-center gap-3 border border-amber-300"
                        >
                            <span className="text-slate-700 text-lg">📊</span>
                            <span className="tracking-wide">Export Excel</span>
                        </button>
                    </div>
                </div>

                {!data[0] && !isLoaded && (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                        <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mb-6">
                            <span className="text-4xl">📈</span>
                        </div>
                        <h3 className="text-2xl font-bold text-slate-700 mb-4">No Sales Data Available</h3>
                        <p className="text-slate-500 max-w-md">
                            No sales have been recorded for the selected period. Please check back later or adjust your date range.
                        </p>
                    </div>
                )}

                {data[0] && isLoaded && (
                    <>
                        <div className="mb-8">
                            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 mb-8 border border-blue-200/50">
                                <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                                    <span className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                                        <span className="text-white text-sm">📊</span>
                                    </span>
                                    Sales Performance Chart
                                </h3>
                                <div className="bg-white rounded-xl p-4 shadow-lg" style={{ height: "400px" }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart
                                            data={salesData}
                                            margin={{
                                                top: 20,
                                                right: 30,
                                                left: 20,
                                                bottom: 5,
                                            }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                            <XAxis 
                                                dataKey="outlet" 
                                                tick={{ fontSize: 12, fill: '#475569' }}
                                                stroke="#64748b"
                                            />
                                            <YAxis 
                                                tick={{ fontSize: 12, fill: '#475569' }}
                                                stroke="#64748b"
                                            />
                                            <Tooltip 
                                                contentStyle={{
                                                    backgroundColor: '#1e293b',
                                                    border: 'none',
                                                    borderRadius: '12px',
                                                    color: 'white',
                                                    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)'
                                                }}
                                            />
                                            <Bar
                                                dataKey="unit"
                                                fill="url(#blueGradient)"
                                                radius={[4, 4, 0, 0]}
                                            />
                                            <defs>
                                                <linearGradient id="blueGradient" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor="#3b82f6" />
                                                    <stop offset="100%" stopColor="#1e40af" />
                                                </linearGradient>
                                            </defs>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200/50">
                                    <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                                        <span className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                                            <span className="text-white text-sm">🥧</span>
                                        </span>
                                        Sales Distribution
                                    </h3>
                                    <div className="bg-white rounded-xl p-4 shadow-lg flex justify-center">
                                        <PieChart width={400} height={300}>
                                            <Pie
                                                data={salesData}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={({ outlet, percent }) => `${outlet}: ${(percent * 100).toFixed(0)}%`}
                                                outerRadius={100}
                                                fill="#8884d8"
                                                dataKey="unit"
                                            >
                                                {salesData.map((entry, index) => (
                                                    <Cell
                                                        key={`cell-${index}`}
                                                        fill={COLORS[index % COLORS.length]}
                                                    />
                                                ))}
                                            </Pie>
                                            <Tooltip content={<CustomTooltip />} />
                                        </PieChart>
                                    </div>
                                </div>

                                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200/50">
                                    <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                                        <span className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                                            <span className="text-white text-sm">📋</span>
                                        </span>
                                        Detailed Sales Data
                                    </h3>
                                    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                                        <Table
                                            data={salesData}
                                            columns={columns}
                                            pageCount={Math.ceil(salesData.length / 50)}
                                            totalCount={salesData.length}
                                            updatePagination={() => {}}
                                            paginationState={{ page: 1, limit: 50 }}
                                            updateLoaded={() => {}}
                                            hidePagination={salesData.length <= 50}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
